import { useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import { useAuth } from "../context/AuthContext";
import { SectionCard, Bar, Empty } from "../components/shared";
import { inr, pct as pctFmt, fmtDate } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import { Target, Plus, Pencil, Trash2, Check, CheckCircle2, AlertTriangle, Link2, PenLine, Calendar, User, X } from "lucide-react";

const fmtVal = (v, format) => format === "money" ? inr(v) : format === "pct" ? `${Number(v || 0).toFixed(1)}%` : Number(v || 0).toLocaleString("en-IN");

const emptyForm = () => ({
  title: "", description: "", goal_type: "manual", metric: "total_saved",
  target_value: 0, current_value: 0, unit: "", branch: "All",
  period_from: "", period_to: "", points: [], status: "active",
});

export default function Goals() {
  const { meta, branch } = useApp();
  const { user } = useAuth();
  const [goals, setGoals] = useState([]);
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [mine, setMine] = useState(false);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [pointText, setPointText] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    api.get("/goals", { params: { branch, date_from: dateFrom, date_to: dateTo, mine } })
      .then((r) => setGoals(r.data)).finally(() => setLoading(false));
  }, [branch, dateFrom, dateTo, mine]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/goals/metrics").then((r) => setMetrics(r.data)); }, []);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openCreate = () => { setEditId(null); setForm(emptyForm()); setPointText(""); setOpen(true); };
  const openEdit = (g) => {
    setEditId(g.id);
    setForm({ title: g.title, description: g.description || "", goal_type: g.goal_type, metric: g.metric || "total_saved",
      target_value: g.target_value, current_value: g.current_value || 0, unit: g.unit || "", branch: g.branch || "All",
      period_from: g.period_from || "", period_to: g.period_to || "", points: g.points || [], status: g.status || "active" });
    setPointText(""); setOpen(true);
  };

  const addPoint = () => {
    if (!pointText.trim()) return;
    setForm((f) => ({ ...f, points: [...f.points, { text: pointText.trim(), done: false }] }));
    setPointText("");
  };
  const removePoint = (i) => setForm((f) => ({ ...f, points: f.points.filter((_, idx) => idx !== i) }));

  const submit = async () => {
    if (!form.title.trim()) { toast.error("Goal title required"); return; }
    const payload = { ...form, target_value: Number(form.target_value), current_value: Number(form.current_value) };
    try {
      if (editId) { await api.put(`/goals/${editId}`, payload); toast.success("Goal updated"); }
      else { await api.post("/goals", payload); toast.success("Goal created"); }
      setOpen(false); setEditId(null); setForm(emptyForm()); load();
    } catch { toast.error("Failed to save goal"); }
  };

  const remove = async (id) => { await api.delete(`/goals/${id}`); toast.success("Goal deleted"); load(); };

  const togglePoint = async (g, i) => {
    const points = g.points.map((p, idx) => idx === i ? { ...p, done: !p.done } : p);
    await api.put(`/goals/${g.id}`, { points });
    setGoals((gs) => gs.map((x) => x.id === g.id ? { ...x, points } : x));
  };

  const total = goals.length;
  const onTrack = goals.filter((g) => g.on_track).length;
  const achieved = goals.filter((g) => g.progress >= 1).length;
  const atRisk = total - onTrack;

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold text-navy tracking-tight flex items-center gap-2"><Target className="w-7 h-7 text-brand-teal" /> Goals</h1>
          <p className="text-brand-slate text-sm mt-1">Set manual targets or auto-linked goals that track live from your procurement data</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-goal-button" onClick={openCreate} className="gap-2 bg-brand-teal hover:bg-brand-tealdark active:scale-95 transition-transform"><Plus className="w-4 h-4" /> New Goal</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">{editId ? "Edit Goal" : "Create Goal"}</DialogTitle>
              <DialogDescription>Auto-linked goals track progress from live data; manual goals are updated by you.</DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-2 gap-2 mb-1">
              <button type="button" data-testid="goal-type-manual" onClick={() => setF("goal_type", "manual")}
                className={`flex items-center gap-2 rounded-lg border-2 p-3 text-sm font-semibold transition-colors ${form.goal_type === "manual" ? "border-brand-teal bg-teal-50 text-navy" : "border-border text-brand-slate"}`}>
                <PenLine className="w-4 h-4" /> Manual
              </button>
              <button type="button" data-testid="goal-type-auto" onClick={() => setF("goal_type", "auto")}
                className={`flex items-center gap-2 rounded-lg border-2 p-3 text-sm font-semibold transition-colors ${form.goal_type === "auto" ? "border-brand-teal bg-teal-50 text-navy" : "border-border text-brand-slate"}`}>
                <Link2 className="w-4 h-4" /> Auto-linked
              </button>
            </div>

            <div className="space-y-3">
              <div><Label>Goal Title</Label><Input data-testid="goal-title" value={form.title} onChange={(e) => setF("title", e.target.value)} placeholder="e.g. Save ₹2L this quarter" /></div>
              <div><Label>Description</Label><Textarea data-testid="goal-description" value={form.description} onChange={(e) => setF("description", e.target.value)} placeholder="Why this goal matters…" /></div>

              {form.goal_type === "auto" && (
                <div><Label>Linked Metric</Label>
                  <Select value={form.metric} onValueChange={(v) => setF("metric", v)}>
                    <SelectTrigger data-testid="goal-metric"><SelectValue /></SelectTrigger>
                    <SelectContent>{metrics.map((m) => <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>)}</SelectContent>
                  </Select>
                  <p className="text-xs text-brand-slate mt-1">Progress updates automatically from live data within the goal period & branch.</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div><Label>Target Value</Label><Input data-testid="goal-target" type="number" value={form.target_value} onChange={(e) => setF("target_value", e.target.value)} /></div>
                {form.goal_type === "manual"
                  ? <div><Label>Current Value</Label><Input data-testid="goal-current" type="number" value={form.current_value} onChange={(e) => setF("current_value", e.target.value)} /></div>
                  : <div><Label>Unit / Note</Label><Input data-testid="goal-unit" value={form.unit} onChange={(e) => setF("unit", e.target.value)} placeholder="optional" /></div>}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div><Label>Branch</Label>
                  <Select value={form.branch} onValueChange={(v) => setF("branch", v)}>
                    <SelectTrigger data-testid="goal-branch"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="All">All Branches</SelectItem>{meta.branches.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div><Label>Status</Label>
                  <Select value={form.status} onValueChange={(v) => setF("status", v)}>
                    <SelectTrigger data-testid="goal-status"><SelectValue /></SelectTrigger>
                    <SelectContent>{["active", "paused", "done"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div><Label>Period From</Label><Input data-testid="goal-from" type="date" value={form.period_from} onChange={(e) => setF("period_from", e.target.value)} /></div>
                <div><Label>Period To</Label><Input data-testid="goal-to" type="date" value={form.period_to} onChange={(e) => setF("period_to", e.target.value)} /></div>
              </div>

              <div>
                <Label>Action Points</Label>
                <div className="flex gap-2">
                  <Input data-testid="goal-point-input" value={pointText} onChange={(e) => setPointText(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addPoint(); } }} placeholder="Add a sub-point / action item…" />
                  <Button type="button" data-testid="goal-point-add" variant="outline" onClick={addPoint} className="shrink-0"><Plus className="w-4 h-4" /></Button>
                </div>
                <div className="mt-2 space-y-1">
                  {form.points.map((p, i) => (
                    <div key={i} className="flex items-center justify-between text-sm bg-slate-50 rounded-lg px-3 py-1.5">
                      <span className="text-navy">• {p.text}</span>
                      <button type="button" data-testid="goal-point-remove" aria-label={`Remove action point ${p.text}`} onClick={() => removePoint(i)} className="text-slate-400 hover:text-rose-600"><X className="w-3.5 h-3.5" /></button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button data-testid="goal-cancel" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="goal-submit" onClick={submit} className="bg-brand-teal hover:bg-brand-tealdark">{editId ? "Save Changes" : "Create Goal"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 bg-white border border-border rounded-xl p-4">
        <span className="text-sm font-semibold text-brand-slate flex items-center gap-1.5"><Calendar className="w-4 h-4" /> Period</span>
        <Input data-testid="goals-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-auto" />
        <span className="text-brand-slate">→</span>
        <Input data-testid="goals-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-auto" />
        {(dateFrom || dateTo) && <Button data-testid="goals-clear" variant="outline" onClick={() => { setDateFrom(""); setDateTo(""); }} className="h-9 gap-1"><X className="w-4 h-4" /> Clear</Button>}
        <div className="ml-auto flex items-center gap-2">
          <button data-testid="goals-mine-toggle" onClick={() => setMine((m) => !m)}
            className={`flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-full border transition-colors ${mine ? "bg-brand-teal text-white border-brand-teal" : "border-border text-brand-slate"}`}>
            <User className="w-3.5 h-3.5" /> My goals only
          </button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[["Total Goals", total, "navy"], ["On Track", onTrack, "emerald"], ["At Risk", atRisk, "rose"], ["Achieved", achieved, "teal"]].map(([l, v, tone]) => (
          <div key={l} className="bg-white rounded-xl border border-border p-5">
            <div className="text-xs uppercase tracking-widest text-brand-slate font-semibold">{l}</div>
            <div className={`font-heading text-3xl font-bold mt-1 ${tone === "emerald" ? "text-brand-emerald" : tone === "rose" ? "text-rose-600" : tone === "teal" ? "text-brand-teal" : "text-navy"}`}>{v}</div>
          </div>
        ))}
      </div>

      {loading ? <div className="text-brand-slate">Loading goals…</div>
        : goals.length === 0 ? <SectionCard><Empty icon={Target} msg="No goals yet — create your first goal to get started" /></SectionCard>
        : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {goals.map((g) => {
              const format = g.format || "num";
              return (
                <div key={g.id} data-testid="goal-card" className="bg-white rounded-xl border border-border p-5 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${g.goal_type === "auto" ? "bg-teal-100 text-brand-teal" : "bg-slate-100 text-brand-slate"}`}>
                          {g.goal_type === "auto" ? "Auto-linked" : "Manual"}
                        </span>
                        {g.on_track
                          ? <span className="flex items-center gap-1 text-[10px] font-bold text-brand-emerald bg-emerald-50 px-2 py-0.5 rounded-full"><CheckCircle2 className="w-3 h-3" /> On track</span>
                          : <span className="flex items-center gap-1 text-[10px] font-bold text-rose-600 bg-rose-50 px-2 py-0.5 rounded-full"><AlertTriangle className="w-3 h-3" /> At risk</span>}
                      </div>
                      <h3 className="font-heading font-semibold text-navy mt-1.5">{g.title}</h3>
                      {g.description && <p className="text-sm text-brand-slate mt-0.5">{g.description}</p>}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button data-testid="goal-edit" aria-label={`Edit goal ${g.title}`} onClick={() => openEdit(g)} className="text-slate-400 hover:text-brand-teal p-1"><Pencil className="w-4 h-4" /></button>
                      <button data-testid="goal-delete" aria-label={`Delete goal ${g.title}`} onClick={() => remove(g.id)} className="text-slate-400 hover:text-rose-600 p-1"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-brand-slate">{g.goal_type === "auto" ? g.metric_label : "Progress"}</span>
                      <span className="font-semibold text-navy tabular-nums">{fmtVal(g.current_value, format)} <span className="text-brand-slate font-normal">/ {fmtVal(g.target_value, format)}</span></span>
                    </div>
                    <Bar value={g.progress} warn={!g.on_track} />
                    <div className="flex justify-between text-xs text-brand-slate mt-1">
                      <span>{Math.round((g.progress || 0) * 100)}% {g.direction === "down" ? "(lower is better)" : ""}</span>
                      <span>{g.branch}</span>
                    </div>
                  </div>

                  {g.points?.length > 0 && (
                    <div className="space-y-1 pt-1 border-t border-border">
                      {g.points.map((p, i) => (
                        <button key={i} data-testid="goal-point" onClick={() => togglePoint(g, i)} className="flex items-center gap-2 text-sm w-full text-left">
                          <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${p.done ? "bg-brand-teal border-brand-teal" : "border-slate-300"}`}>{p.done && <Check className="w-3 h-3 text-white" />}</span>
                          <span className={p.done ? "line-through text-slate-400" : "text-navy"}>{p.text}</span>
                        </button>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center justify-between text-xs text-brand-slate pt-1">
                    <span className="flex items-center gap-1"><User className="w-3 h-3" /> {g.owner_name}</span>
                    <span>{g.period_from || g.period_to ? `${fmtDate(g.period_from)} → ${fmtDate(g.period_to)}` : "No date set"}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
    </div>
  );
}
